import { APP_SCORING_MAX_SCORE } from '@/constants';
import {
  getCompletionDatePolicy,
  getScoreDescriptionPolicy,
  getScorePass,
  getSimulationTypeFromV2FlagsPolicy,
  mapScoreCategoryPolicy,
  normalizeRatingCutoffs,
  resolveCertificationIdFromScorecard,
  resolveDateCompletedPolicy,
  resolveFallbackSessionIdPolicy,
  resolvePassPolicy,
  resolveScoreTypePolicy,
  resolveSimulationIdPolicy,
  resolveSimulationTypePolicy,
  resolveUuidPolicy,
  resolveV2DateCompletedPolicy,
  resolveV2MetadataPolicy,
  TDateCompletedStrategy,
  TFallbackStrategy,
  TPassStrategy,
  TSimulationTypeStrategy,
  TUuidStrategy,
} from '@/domains/scoring';
import {
  ApiAvailables,
  ApiScoreCard,
  ApiScoreCardv2,
  ISimulationScore,
  TNormalizedRatingCutoffs,
  TScoreType,
} from '@/types';

// ================================
// Types
// ================================

/**
 * Local union for scorecard payload variants.
 *
 * Why this alias exists:
 * - Keeps function signatures concise.
 * - Makes discriminated handling (`isApiScoreCardV2`) explicit.
 * - Preserves compatibility for both legacy and v2 scorecard contracts.
 */
type TApiScoreCardAny = ApiScoreCard | ApiScoreCardv2;

/**
 * Configuration for mapping a single API scorecard into the frontend score model.
 *
 * The single-score mapper intentionally supports multiple strategies because the app has two
 * legitimate use cases:
 * 1. Hydrating the store from a collection of scorecards returned by broader API endpoints.
 * 2. Mapping one freshly-fetched scorecard in the post-simulation flow, where the nested
 *    payload may be the preferred source of truth.
 */
export interface IMapApiScorecardToFrontendOptions {
  /** Optional certification collection used to infer certification and simulation metadata. */
  apiAvailables?: ApiAvailables[];

  /** Explicit certification id override when the caller already knows the target certification. */
  certificationId?: string;

  /** Determines how `dateCompleted` is resolved. */
  dateCompletedStrategy?: TDateCompletedStrategy;

  /** Determines whether sparse values are preserved or normalized to store defaults. */
  fallbackStrategy?: TFallbackStrategy;

  /** Determines whether pass/fail is read directly or derived. */
  passStrategy?: TPassStrategy;

  /** Determines whether simulation type comes from resolved metadata or the score payload. */
  simulationTypeStrategy?: TSimulationTypeStrategy;

  /** Determines whether the mapped `uuid` comes from score data or session data. */
  uuidStrategy?: TUuidStrategy;
}

/**
 * Extracts and safely accesses score data from the nested scorecard structure.
 *
 * @param apiScorecard - Raw API scorecard object
 * @returns Extracted scorecard_json
 */
const extractScoreDataJson = (apiScorecard: ApiScoreCard) => {
  // The backend nests frontend-facing score data under `local_data.scorecard_json`.
  // This helper centralizes access so the rest of the file does not repeatedly walk the
  // same optional chain.
  const localData = apiScorecard.local_data;
  const scorecardJson = localData?.scorecard_json;
  return scorecardJson;
};

/**
 * Type guard that discriminates between v1 and v2 scorecard payloads.
 */
const isApiScoreCardV2 = (apiScorecard: TApiScoreCardAny): apiScorecard is ApiScoreCardv2 => {
  return 'cert_pass' in apiScorecard;
};

/**
 * Resolves certification id for v2 payloads.
 *
 * Resolution order:
 * 1) explicit override from caller
 * 2) infer from availables when present
 * 3) fallback to v2 certification_id field
 */
const resolveCertificationIdFromV2 = ({
  apiAvailables,
  apiScorecard,
  explicitCertificationId,
}: {
  apiAvailables: ApiAvailables[];
  apiScorecard: ApiScoreCardv2;
  explicitCertificationId?: string;
}): string => {
  if (explicitCertificationId) {
    return explicitCertificationId;
  }

  if (apiAvailables.length > 0) {
    return resolveCertificationIdFromScorecard(apiScorecard, apiAvailables) || '';
  }

  return apiScorecard.certification_id?.toString() || '';
};

/**
 * Resolves the certification id for a mapped scorecard.
 *
 * Resolution order is intentional:
 * 1. Use an explicit override if the caller already knows the certification.
 * 2. Derive it from `apiAvailables` when hydrating from broader API collections.
 * 3. Fall back to the nested score payload if no collection metadata is available.
 */
const resolveCertificationId = ({
  apiAvailables,
  apiScorecard,
  explicitCertificationId,
  scoreData,
}: {
  apiAvailables: ApiAvailables[];
  apiScorecard: ApiScoreCard;
  explicitCertificationId?: string;
  scoreData: ISimulationScore | null;
}): string => {
  // Allow the caller to bypass inference entirely.
  if (explicitCertificationId) {
    return explicitCertificationId;
  }

  // Batch/store mapping usually has access to `apiAvailables`, so use the shared derivation helper.
  if (apiAvailables.length > 0) {
    return resolveCertificationIdFromScorecard(apiScorecard, apiAvailables) || '';
  }

  // Single-score view mapping may only have the nested payload available.
  return scoreData?.certificationId || '';
};

/**
 * Maps a single `ApiScoreCard` into the frontend `ISimulationScore` model.
 *
 * This is the canonical mapping entry point for individual scorecards. It is designed to support
 * both of the app's current scorecard flows without forcing either one to give up its existing
 * semantics.
 *
 * The mapper works in four broad phases:
 * 1. Extract the nested score payload.
 * 2. Resolve identifiers and related metadata.
 * 3. Apply caller-selected strategies for fields that differ across app flows.
 * 4. Return a normalized `ISimulationScore` object for UI/state consumption.
 */
export function mapApiScorecardToFrontend(
  apiScorecard: TApiScoreCardAny,
  {
    apiAvailables = [],
    certificationId,
    dateCompletedStrategy = 'api-timestamps',
    fallbackStrategy = 'store-defaults',
    passStrategy = 'derived',
    simulationTypeStrategy = 'resolved-simulation',
    uuidStrategy = 'session-id',
  }: IMapApiScorecardToFrontendOptions = {},
): ISimulationScore {
  // =================================================================
  // V2 Mapping Path
  // =================================================================
  // Keep this branch separate so v2-specific fields and semantics stay
  // isolated from legacy scorecard handling.
  if (isApiScoreCardV2(apiScorecard)) {
    // Determine certification linkage before simulation metadata lookups.
    const resolvedCertificationId = resolveCertificationIdFromV2({
      apiAvailables,
      apiScorecard,
      explicitCertificationId: certificationId,
    });

    // Session id fallback policy differs by mapper strategy.
    const sessionId = resolveFallbackSessionIdPolicy(apiScorecard.session_id, fallbackStrategy);
    const simulationId = apiScorecard.simulation_id?.toString() || '';

    // Resolve related certification object for metadata enrichment.
    const certification = apiAvailables.find((available) => {
      return available.id.toString() === resolvedCertificationId;
    });

    // Flatten all candidate simulations under this certification so simulation
    // matching works across regular/practice/certification slots.
    const certSimulations = certification?.simulations || [];
    const allCertSimulations = [
      ...certSimulations,
      ...(certification?.certificationPractice ? [certification.certificationPractice] : []),
      ...(certification?.certificationSimulation ? [certification.certificationSimulation] : []),
    ];

    // Resolve the current simulation for title/type enrichment.
    const simulation = allCertSimulations.find((candidate) => {
      return candidate.id.toString() === simulationId;
    });

    // Build normalized metadata block for v2-only fields.
    const metadata = resolveV2MetadataPolicy({ apiScorecard, certification, simulation });

    // Infer simulation type from v2 flags and project into score-shaped object
    // used by derived pass policy.
    const inferredSimulationType = getSimulationTypeFromV2FlagsPolicy(apiScorecard);
    const scoreDataForPass: ISimulationScore = {
      ...metadata,
      categories: (apiScorecard.categories || []).map(mapScoreCategoryPolicy),
      dateCompleted: apiScorecard.date_completed || new Date().toISOString(),
      pass: undefined,
      score: apiScorecard.score ?? undefined,
      scoreMax: apiScorecard.score_max ?? undefined,
      simulationId,
      simulationType: inferredSimulationType,
      uuid: sessionId.toString(),
    };

    // Resolve final simulation type according to configured strategy.
    const simulationType = resolveSimulationTypePolicy({
      fallbackStrategy,
      scoreSimulationType: scoreDataForPass.simulationType,
      simulationType: simulation?.type,
      simulationTypeStrategy,
    });

    // Normalize optional compliance reminders payload.
    const complianceReminders = apiScorecard.compliance_reminders
      ? {
          title: apiScorecard.compliance_reminders.title,
          items: apiScorecard.compliance_reminders.items,
        }
      : undefined;

    // Derive pass/fail using shared policy helper input shape.
    const passFail = getScorePass(
      typeof apiScorecard.cert_pass === 'boolean' ? apiScorecard.cert_pass : undefined,
      simulationType,
      scoreDataForPass,
    );

    // Normalize runtime rating cutoffs (L1-L4 percentage bands) from API payload.
    // Falls back to static constants if payload is null/invalid.
    const runtimeRatingCutoffs = normalizeRatingCutoffs(
      apiScorecard.rounded_rating_cutoffs,
    );

    // Return normalized v2 score shape with strategy-sensitive fallbacks.
    return {
      ...metadata,
      categories: (apiScorecard.categories || []).map(mapScoreCategoryPolicy),
      certificationId: resolvedCertificationId,
      complianceReminders,
      dateCompleted: resolveV2DateCompletedPolicy({
        dateCompleted: apiScorecard.date_completed || undefined,
        dateCompletedStrategy,
      }),
      description: fallbackStrategy === 'store-defaults' ? '' : undefined,
      id: sessionId,
      pass: resolvePassPolicy({
        derivedPass: passFail,
        fallbackStrategy,
        passStrategy,
        rawCertPass:
          typeof apiScorecard.cert_pass === 'boolean' ? apiScorecard.cert_pass : undefined,
      }),
      runtimeRatingCutoffs,
      score: apiScorecard.score ?? (fallbackStrategy === 'store-defaults' ? 0 : undefined),
      scoreMax:
        apiScorecard.score_max ??
        (fallbackStrategy === 'store-defaults' ? APP_SCORING_MAX_SCORE : undefined),
      sessionId,
      sharedWithManager:
        typeof apiScorecard.shared_with_manager === 'boolean'
          ? apiScorecard.shared_with_manager
          : undefined,
      simulationId,
      simulationType,
      type: resolveScoreTypePolicy({
        fallbackStrategy,
        scoreType: apiScorecard.type as TScoreType | null | undefined,
      }),
      uuid: uuidStrategy === 'session-id' ? sessionId.toString() : sessionId.toString(),
    };
  }

  // =================================================================
  // Legacy (v1) Mapping Path
  // =================================================================

  // Pull the nested scorecard JSON and its `score` object once so every downstream helper can
  // work against a consistent local reference.
  const scoreCardJsonData = extractScoreDataJson(apiScorecard);
  const scoreData = scoreCardJsonData?.score || null;

  // Resolve the certification id before looking up related certification metadata.
  const resolvedCertificationId = resolveCertificationId({
    apiAvailables,
    apiScorecard,
    explicitCertificationId: certificationId,
    scoreData,
  });

  // Store mode preserves the historical `-1` fallback for missing session ids. Raw mode uses `0`
  // simply to avoid introducing the store-specific sentinel into sparse one-off score mappings.
  const sessionId = resolveFallbackSessionIdPolicy(apiScorecard.session_id, fallbackStrategy);

  // Resolve the simulation id according to the current fallback mode.
  const simulationId = resolveSimulationIdPolicy({
    apiScenarioId: apiScorecard.scenario_id,
    fallbackStrategy,
    scoreSimulationId: scoreData?.simulationId,
  });

  // Match the owning certification from `apiAvailables` so related simulation metadata can be used
  // for simulation type resolution and pass/fail derivation.
  const certification = apiAvailables.find((available) => {
    return available.id.toString() === resolvedCertificationId;
  });

  // A certification can expose simulations in a few slots, so collect them into one searchable list.
  const certSimulations = certification?.simulations || [];
  const allCertSimulations = [
    ...certSimulations,
    ...(certification?.certificationPractice ? [certification.certificationPractice] : []),
    ...(certification?.certificationSimulation ? [certification.certificationSimulation] : []),
  ];

  // Find the simulation that corresponds to the current scorecard so we can derive `simulationType`
  // when the caller requests metadata-based resolution.
  const simulation = allCertSimulations.find((candidate) => {
    return candidate.id.toString() === simulationId;
  });

  // Decide which simulation type should ultimately be exposed on the mapped score.
  const simulationType = resolveSimulationTypePolicy({
    fallbackStrategy,
    scoreSimulationType: scoreData?.simulationType,
    simulationType: simulation?.type,
    simulationTypeStrategy,
  });

  // Normalize runtime rating cutoffs (L1-L4 percentage bands) from API payload.
  // For v1 payloads, this passes null and returns the fallback constants.
  const runtimeRatingCutoffs = normalizeRatingCutoffs(null);

  // Final normalized legacy score object.
  return {
    // Stable API record identifier.
    id: apiScorecard.id,

    // Configurable frontend identifier, historically either `session_id` or nested score id.
    uuid: resolveUuidPolicy({
      scoreId: scoreData?.id,
      scoreUuid: scoreData?.uuid,
      sessionId,
      uuidStrategy,
    }),

    // Simulation linkage for routing and related UI.
    simulationId,
    simulationType,

    // Certification linkage for score grouping and downstream lookups.
    certificationId: resolvedCertificationId,
    certificationTitle: scoreCardJsonData?.certificationTitle,
    sessionId,

    // Score type is preserved when present; store mode defaults to `numeric` for compatibility.
    type: resolveScoreTypePolicy({
      fallbackStrategy,
      scoreType: scoreData?.type,
    }),

    // Pass/fail handling is intentionally strategy-driven because different callers want slightly
    // different semantics here.
    pass: resolvePassPolicy({
      derivedPass: getScorePass(scoreCardJsonData?.certPass, simulationType, scoreData),
      fallbackStrategy,
      passStrategy,
      rawCertPass: scoreCardJsonData?.certPass,
    }),

    // Runtime rating cutoffs (L1-L4 percentage bands) for percentage-based scoring.
    runtimeRatingCutoffs,

    // Completion date can come either from top-level API timestamps or the nested score payload.
    dateCompleted: resolveDateCompletedPolicy({
      apiCompletionDate: getCompletionDatePolicy(apiScorecard),
      dateCompletedStrategy,
      scoreDateCompleted: scoreData?.dateCompleted?.toString(),
    }),

    // Raw numeric score values, optionally normalized for store consumers.
    score: scoreData?.score ?? (fallbackStrategy === 'store-defaults' ? 0 : undefined),
    scoreMax:
      scoreData?.scoreMax ??
      (fallbackStrategy === 'store-defaults' ? APP_SCORING_MAX_SCORE : undefined),

    // Description is preserved when present. Store mode uses the legacy empty-string fallback.
    description:
      scoreData?.description ??
      (fallbackStrategy === 'store-defaults' ? getScoreDescriptionPolicy(scoreData) : undefined),

    // Whether the scorecard has been shared with a manager.
    sharedWithManager: apiScorecard.is_shared_with_manager,

    // Optional display metadata that can be inferred during mapping.
    simulationTitle: simulation?.title,

    // Normalize each score category into the UI-friendly frontend format.
    categories: (scoreData?.categories || []).map(mapScoreCategoryPolicy),
  };
}

/**
 * Maps API scorecards to frontend ISimulationScore format.
 *
 * @param apiAvailables - Array of ApiAvailables from the API
 * @param apiScorecards - Array of ApiScoreCard from the API
 *
 * @returns Array of ISimulationScore formatted for the frontend
 */
export function mapApiScorecardsToFrontend({
  apiAvailables,
  apiScorecards,
}: {
  apiAvailables: ApiAvailables[];
  apiScorecards: TApiScoreCardAny[];
}): ISimulationScore[] {
  // ---------------------------------------------------------------
  // Phase 1: Build a simulation-scoped cutoff lookup table.
  // ---------------------------------------------------------------
  // Why this phase exists:
  // - API responses can mix legacy (v1) and v2 scorecards.
  // - Only v2 scorecards expose `rounded_rating_cutoffs`.
  // - All scorecards that represent the same simulation should share one
  //   runtime cutoff definition during frontend rendering.
  //
  // Important behavior:
  // - We key by `simulation_id`, not by scorecard/session id.
  // - We only ingest non-null, non-empty cutoff arrays.
  // - Different simulations can naturally hold different cutoff sets.
  // - This is intentionally computed BEFORE we map scorecards so the mapping
  //   pass can apply simulation-level cutoffs in a single pass.
  const runtimeCutoffsBySimulationId = new Map<string, TNormalizedRatingCutoffs>();

  apiScorecards.forEach((apiScorecard) => {
    // Legacy (v1) payloads do not include runtime cutoff metadata, so they
    // cannot act as a source of truth for inference.
    if (!isApiScoreCardV2(apiScorecard)) return;

    // Treat null/empty cutoff payloads as non-authoritative values.
    // We only promote concrete cutoff arrays into the shared lookup.
    if (
      !Array.isArray(apiScorecard.rounded_rating_cutoffs) ||
      apiScorecard.rounded_rating_cutoffs.length === 0
    ) {
      return;
    }

    // Normalize to string because mapped score objects also store ids as strings.
    // Using a consistent key type avoids mismatch during lookup.
    const simulationId = apiScorecard.simulation_id?.toString() || '';

    // Skip malformed payloads where simulation linkage is missing.
    if (!simulationId) return;

    // Normalize API shape into the frontend cutoff format before storing.
    // If multiple v2 entries exist for the same simulation, later entries win,
    // which keeps behavior deterministic with respect to array order.
    runtimeCutoffsBySimulationId.set(
      simulationId,
      normalizeRatingCutoffs(apiScorecard.rounded_rating_cutoffs),
    );
  });

  // ---------------------------------------------------------------
  // Phase 2: Map each scorecard, then apply simulation-level cutoff override.
  // ---------------------------------------------------------------
  // The base mapper still performs all canonical score transformation.
  // We then layer inferred cutoffs on top so legacy and sparse entries inherit
  // the simulation-specific runtime cutoffs discovered in Phase 1.
  const mappedScores = apiScorecards.map((apiScorecard) => {
    // Store hydration uses deterministic strategy defaults so downstream
    // consumers receive stable semantics across endpoint shapes.
    const mappedScore = mapApiScorecardToFrontend(apiScorecard, {
      apiAvailables,
      dateCompletedStrategy: 'api-timestamps',
      fallbackStrategy: 'store-defaults',
      passStrategy: 'derived',
      simulationTypeStrategy: 'resolved-simulation',
      uuidStrategy: 'session-id',
    });

    // Lookup is intentionally by the mapped score's `simulationId` so v1 and v2
    // entries converge on the same simulation-scoped cutoff set.
    const inferredCutoffs = runtimeCutoffsBySimulationId.get(mappedScore.simulationId);

    // No inferred value means we preserve whatever the single-score mapper
    // already produced (API-derived cutoffs for v2, defaults for legacy/sparse).
    if (!inferredCutoffs) {
      return mappedScore;
    }

    // Overwrite with the inferred simulation-level cutoff set so all entries
    // for the same simulation render against identical runtime bands.
    return {
      ...mappedScore,
      runtimeRatingCutoffs: inferredCutoffs,
    };
  });

  return mappedScores;
}
